import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articles, categories } from '@/mocks/articles';
import { siteInfo } from '@/mocks/siteInfo';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  const filteredArticles = useMemo(
    () => articles.filter((a) => a.category === slug),
    [slug]
  );

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="chigua-container pt-24 pb-16 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900">分类未找到</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 mt-4 inline-block">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - ${siteInfo.name}`,
    description: category.description,
    url: `https://itxkf.cn/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '吃瓜黑料',
      url: 'https://itxkf.cn',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(webpageSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="chigua-container">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />

            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100">
                <i className={`${category.icon} text-xl text-accent-600`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">{category.description}</p>
              </div>
            </div>

            {filteredArticles.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
                  <div className="md:col-span-2">
                    <ArticleCard article={filteredArticles[0]} variant="featured" />
                  </div>
                  <div className="flex flex-col gap-4">
                    {filteredArticles.slice(1, 3).map((article) => (
                      <ArticleCard key={article.id} article={article} variant="horizontal" />
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {filteredArticles.slice(3).map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-inbox-line text-3xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            )}

            <div className="mt-12 flex items-center justify-center gap-2">
              <h4 className="text-sm font-heading font-bold text-foreground-700">
                浏览更多分类：
              </h4>
              {categories
                .filter((c) => c.slug !== slug)
                .map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="px-4 py-1.5 text-sm rounded-full bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-700 transition-colors whitespace-nowrap no-underline"
                  >
                    {cat.name}
                  </Link>
                ))}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}